import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
  ACTIVITY_EVENTS, ACTIVITY_WRITE_MS, KEYS, endSession, loginUrl, markActivity, needsRefresh, readSession,
  saveSession, sessionPhase,
} from "@/lib/session";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmt = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// Dipasang di Layout, jadi hanya aktif di halaman yang butuh login (bukan /login atau /lapor-siport).
export function SessionGuard() {
  const [view, setView] = useState({ phase: "ok", idleLeft: 0, maxLeft: 0 });
  const [maxWarnSeen, setMaxWarnSeen] = useState(false);
  const phaseRef = useRef("ok");
  const lastWrite = useRef(0);
  const refreshing = useRef(false);

  const tick = useCallback(() => {
    const s = sessionPhase({ now: Date.now(), ...readSession() });
    if (s.phase === "expired") {
      endSession("sesi");
      return;
    }
    phaseRef.current = s.phase;
    setView((prev) => (prev.phase === "ok" && s.phase === "ok" ? prev : s));
  }, []);

  const refresh = useCallback(async () => {
    if (refreshing.current) return;
    refreshing.current = true;
    const sentAt = Date.now();
    try {
      const res = await api.post("/auth/refresh");
      saveSession(res.data, sentAt);
    } catch {
      // 401 sudah ditangani interceptor (logout). 404/5xx/jaringan diabaikan agar pengguna tidak terlempar.
    } finally {
      refreshing.current = false;
    }
  }, []);

  // Aktivitas dicatat paling sering tiap 15 detik; setiap kali dicatat, token diperpanjang bila umurnya >= 5 menit.
  const activity = useCallback(
    (force = false) => {
      // Saat peringatan idle tampil, hanya tombol "Tetap masuk" yang dihitung.
      if (!force && phaseRef.current === "idle-warn") return;
      const now = Date.now();
      if (!force && now - lastWrite.current < ACTIVITY_WRITE_MS) return;
      lastWrite.current = now;
      markActivity(now);
      if (needsRefresh(now, readSession(now).tokenAt)) refresh();
    },
    [refresh]
  );

  useEffect(() => {
    if (!localStorage.getItem(KEYS.activity)) markActivity();
    tick();
    const timer = setInterval(tick, 1000);
    const onActivity = () => activity();
    const onStorage = (e) => {
      if (e.key !== KEYS.logout || !e.newValue) return;
      let reason = "keluar";
      try {
        reason = JSON.parse(e.newValue).reason;
      } catch {
        // nilai rusak: anggap logout biasa
      }
      window.location.href = loginUrl(reason);
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { capture: true, passive: true }));
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    window.addEventListener("storage", onStorage);
    return () => {
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity, { capture: true }));
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
      window.removeEventListener("storage", onStorage);
    };
  }, [tick, activity]);

  const stayIn = () => {
    activity(true);
    tick();
  };

  return (
    <>
      <AlertDialog open={view.phase === "idle-warn"}>
        <AlertDialogContent data-testid="session-idle-warning">
          <AlertDialogHeader>
            <AlertDialogTitle>Sesi akan berakhir</AlertDialogTitle>
            <AlertDialogDescription>
              Tidak ada aktivitas selama hampir 30 menit. Anda akan keluar otomatis dalam{" "}
              <span className="font-semibold tabular-nums text-brand-deep">{fmt(view.idleLeft)}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => endSession("keluar")} data-testid="session-logout-button">
              Keluar
            </AlertDialogCancel>
            <AlertDialogAction onClick={stayIn} data-testid="session-stay-button">
              Tetap masuk
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={view.phase === "max-warn" && !maxWarnSeen}>
        <AlertDialogContent data-testid="session-max-warning">
          <AlertDialogHeader>
            <AlertDialogTitle>Batas sesi 12 jam</AlertDialogTitle>
            <AlertDialogDescription>
              Demi keamanan, sesi dibatasi 12 jam sejak masuk. Sesi berakhir dalam{" "}
              <span className="font-semibold tabular-nums text-brand-deep">{fmt(view.maxLeft)}</span>. Simpan
              pekerjaan Anda, lalu masuk kembali.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setMaxWarnSeen(true)} data-testid="session-max-ok">
              Mengerti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
